import React, { useState, useEffect } from "react";
import Styles from "./ContractCalculator.module.css";
import {
  Add,
  ArrowRightAlt,
  BarChart,
  Search,
  SwapVert,
  Business,
  PersonOff,
  TrendingUp,
} from "@mui/icons-material";
import AddContractModal from "./AddContractModal";
import {
  getActiveContracts,
  getInactiveContracts,
} from "../../Services/banking/BankingCalc";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "inactive", label: "Inactive" },
];

const SelectionContainer = ({ onSelect, selectedId, isAggregateActive, onSelectAggregate }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [contracts, setContracts] = useState([]);

  useEffect(() => {
    const toArray = (res) => {
      const d = res?.data;
      if (Array.isArray(d)) return d;
      if (Array.isArray(d?.data)) return d.data;
      if (Array.isArray(d?.contracts)) return d.contracts;
      if (Array.isArray(d?.items)) return d.items;
      return [];
    };

    const fetchContracts = async () => {
      const [activeRes, inactiveRes] = await Promise.all([
        getActiveContracts(),
        getInactiveContracts(),
      ]);
      const active = toArray(activeRes).map((c) => ({ ...c, status: "active" }));
      const inactive = toArray(inactiveRes).map((c) => ({ ...c, status: "inactive" }));
      setContracts([...active, ...inactive]);
    };
    fetchContracts();
  }, []);

  const filtered = contracts.filter((c) => {
    const matchFilter = activeFilter === "all" || c.status === activeFilter;
    const q = searchQuery.toLowerCase();
    const matchSearch =
      !q ||
      c.supplier?.toLowerCase().includes(q) ||
      c.receiver?.toLowerCase().includes(q) ||
      c.loa_number?.toLowerCase().includes(q) ||
      c.trader?.toLowerCase().includes(q);
    return matchFilter && matchSearch;
  });

  const counts = {
    all: contracts.length,
    active: contracts.filter((c) => c.status === "active").length,
    inactive: contracts.filter((c) => c.status === "inactive").length,
  };

  return (
    <>
      <div className={Styles.selectionWrapper}>
        <div className={Styles.containerHeader}>
          <div>
            <div className={Styles.contractHead}>
              Contracts{" "}
              <span className={Styles.contractCount}>{contracts.length}</span>
            </div>
            <div className={Styles.contractSubtitle}>
              Banking module — CSPDCL
            </div>
          </div>
          {/* <div
            className={`activeButton_type_2 ${Styles.addNewButton}`}
            onClick={() => setModalOpen(true)}
          >
            <Add style={{ fontSize: "14px" }} />
            <span>Add New</span>
          </div> */}
        </div>

        <div className={Styles.sectionPad}>
          <div
            className={`${Styles.aggregateCard} ${isAggregateActive ? Styles.aggregateCardSelected : ""}`}
            onClick={onSelectAggregate}
          >
            <div className={Styles.aggregateIconBox}>
              <BarChart className={Styles.aggregateIcon} />
            </div>
            <div className={Styles.aggregateText}>
              <span className={Styles.aggregateTitle}>Aggregate view</span>
              <span className={Styles.aggregateSubtitle}>
                All {contracts.length} contracts combined
              </span>
            </div>
            <ArrowRightAlt className={Styles.aggregateArrow} />
          </div>

          <div className={Styles.searchSection}>
            <Search className={Styles.searchIcon} />
            <input
              className={Styles.searchInput}
              placeholder="Search by party, LoA, trader…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className={Styles.filterTabs}>
            {FILTERS.map((tab) => (
              <button
                key={tab.key}
                className={`${Styles.filterTab} ${activeFilter === tab.key ? Styles.filterTabActive : ""}`}
                onClick={() => setActiveFilter(tab.key)}
              >
                {tab.label}
                <span
                  className={`${Styles.filterBadge} ${
                    activeFilter === tab.key
                      ? Styles.filterBadgeSelected
                      : tab.key === "active"
                        ? Styles.filterBadgeGreen
                        : Styles.filterBadgeGrey
                  }`}
                >
                  {counts[tab.key]}
                </span>
              </button>
            ))}
          </div>

          <div className={Styles.listMeta}>
            <span className={Styles.listMetaLabel}>
              {filtered.length} contracts
            </span>
            <button className={Styles.sortBtn}>
              <SwapVert style={{ fontSize: "13px" }} /> Sort
            </button>
          </div>
        </div>

        <div className={`${Styles.contractList} scroll_bar_y`}>
          {filtered.length === 0 ? (
            <div className={Styles.emptyState}>
              No contracts match your search
            </div>
          ) : (
            filtered.map((contract, idx) => {
              const isActive = contract.status === "active";
              return (
                <div
                  key={idx}
                  onClick={() => onSelect(contract.id)}
                  className={`${Styles.contractCard} ${
                    isActive
                      ? Styles.contractCardActive
                      : Styles.contractCardInactive
                  } ${selectedId === contract.id ? Styles.contractCardSelected : ""}`}
                >
                  <div className={Styles.cardBody}>

                    {/* LoA row — small caption */}
                    <div className={Styles.loaRow}>
                      <div className={Styles.loaRef}>{contract.loa_number}</div>
                      <span
                        className={`${Styles.statusPill} ${
                          isActive ? Styles.pillActive : Styles.pillInactive
                        }`}
                      >
                        {contract.status.toUpperCase()}
                      </span>
                    </div>

                    {/* Party hero block */}
                    <div className={Styles.partyHero}>
                      <span
                        className={Styles.partyHeroName}
                        title={contract.supplier}
                      >
                        {contract.supplier}
                      </span>
                      <div className={Styles.partyHeroArrow}>
                        <ArrowRightAlt
                          style={{ fontSize: "16px", color: "var(--primary-color-background)" }}
                        />
                      </div>
                      <span
                        className={Styles.partyHeroName}
                        title={contract.receiver}
                      >
                        {contract.receiver}
                      </span>
                    </div>

                    {/* Metrics row — period pills + banking factor */}
                    <div className={Styles.metricsRow}>
                      <div className={Styles.periodPills}>
                        <span className={Styles.periodPill}>
                          <span className={Styles.periodPillLabel}>Supply</span>
                          <span className={Styles.periodPillCount}>
                            {contract.supply_period_count}
                          </span>
                        </span>
                        <span className={Styles.periodPill}>
                          <span className={Styles.periodPillLabel}>Return</span>
                          <span
                            className={`${Styles.periodPillCount} ${
                              isActive ? Styles.metricWarn : Styles.metricDim
                            }`}
                          >
                            {contract.return_period_count}
                          </span>
                        </span>
                      </div>
                      <span
                        className={`${Styles.bankingFactor} ${
                          isActive ? Styles.bankingFactorActive : Styles.bankingFactorDim
                        }`}
                      >
                        <TrendingUp style={{ fontSize: "13px" }} />
                        {contract.banking_factor_pct}%
                      </span>
                    </div>

                    {/* Footer */}
                    <div className={Styles.cardFooter}>
                      {contract.trader ? (
                        <span className={Styles.traderBadge}>
                          <Business style={{ fontSize: "11px" }} />{" "}
                          {contract.trader}
                        </span>
                      ) : (
                        <span className={Styles.noTrader}>
                          <PersonOff style={{ fontSize: "12px" }} /> No trader
                          assigned
                        </span>
                      )}
                      <span
                        className={`${Styles.viewLink} ${
                          !isActive ? Styles.viewLinkDim : ""
                        }`}
                      >
                        View details{" "}
                        <ArrowRightAlt style={{ fontSize: "13px" }} />
                      </span>
                    </div>

                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <AddContractModal
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
      />
    </>
  );
};

export default SelectionContainer;
